import React, { useState } from "react";
import {
  Box,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  useTheme,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { faEye } from "@fortawesome/free-solid-svg-icons";
// import { faEdit } from "@fortawesome/free-solid-svg-icons"; // used by commented-out Edit overlay btn
import msgIcon from "../../../../assets/msg-icon.png";
import checkIcon from "../../../../assets/check.png";
import emptyImage from "../../../../assets/empty-image.png";
import AppButton from "../../common/buttons/AppButton";
import { OrderMockup } from "../../../../models/Order/OrderMockup";
import pendingIcon from "../../../../assets/pending.svg";
import ImageAPI from "../../../../api/ImageAPI";
import { useNotification } from "../../../../utils/NotificationsContext";
import { observer } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomTypography from "../../common/form/CustomTypography";
import dayjs from "dayjs";
import infoIcon from "../../../../assets/infoiconmockup.png";
import ImageUpload from "../../common/upload/ImageUpload";
import { useStores } from "../../../../use-stores";
import FormTextField from "../../common/form/FormTextField";
import rejectIcon from "../../../../assets/reject.png";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import { isPdfUrl } from "../../../../utils/urlHelper";
interface SourcingOpsProductMockupsProps {
  mockups: OrderMockup[];
  selectedMockup: number | null;
  setSelectedMockup: (id: number) => void;
  handleCommentClick: (id: number) => void;
  handleDeleteClick: (id: number) => void;
  handleMockupPriorityChange: (id: number) => void;
  openImagePreview: (image_url: string) => void;
  archived: number;
  canDeleteMockup: boolean;
  uploadMockupUpdate: (mockupId: number, uploadedUrl: string[]) => void;
  onGenerateClick?: () => void;
  onGenerateMockupClick?: (mockupId: number) => void;
  handleApproveMockup: (
    mockupId: number,
    imageUrl?: string,
    approvalNote?: string,
  ) => void;
  handleRejectMockup: (
    mockupId: number,
    rejectionNote?: string,
    imageUrl?: string,
  ) => void;
}

const SourcingOpsProductMockups: React.FC<SourcingOpsProductMockupsProps> = ({
  mockups,
  selectedMockup,
  setSelectedMockup,
  handleCommentClick,
  handleDeleteClick,
  handleMockupPriorityChange,
  openImagePreview,
  archived,
  canDeleteMockup,
  uploadMockupUpdate,
  onGenerateClick,
  onGenerateMockupClick,
  handleApproveMockup,
  handleRejectMockup,
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const { projectSourcingOpsStore, userStore } = useStores();
  const [popupState, setPopupState] = useState<
    false | { mode: "approve" | "reject"; mockupId: number }
  >(false);

  const handleCloseApprovalPopup = () => {
    setPopupState(false);

    projectSourcingOpsStore.setApprovalNotes("");
    projectSourcingOpsStore.setRejectionNotes("");
    projectSourcingOpsStore.setApprovalImages([]);
  };

  const handleOpenApprovalPopup = (mockupId: number) => {
    setSelectedMockup(mockupId);
    setPopupState({ mode: "approve", mockupId });
  };

  const handleOpenRejectPopup = (mockupId: number) => {
    setSelectedMockup(mockupId);
    setPopupState({ mode: "reject", mockupId });
  };

  const handleFileChangeAndUploadForMockup = async (
    event: React.ChangeEvent<HTMLInputElement>,
    mockupId: number,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const selectedFilesArray = Array.from(files);

    const imageAPI = new ImageAPI();

    imageAPI
      .uploadImage(selectedFilesArray, "product-images")
      .then((uploadedUrl) => {
        uploadMockupUpdate(mockupId, uploadedUrl);
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        event.target.value = "";
      });
  };

  const getBooleanLikeValue = (value: unknown) => {
    return value === true || value === 1 || value === "1";
  };

  const isRejectedMockup = (mockup: OrderMockup) => {
    const rejected = (mockup as OrderMockup & { rejected?: boolean | number | string }).rejected;

    return getBooleanLikeValue(mockup.is_rejected) || getBooleanLikeValue(rejected);
  };

  const isApprovedMockup = (mockup: OrderMockup) => {
    const rejected = (mockup as OrderMockup & { rejected?: boolean | number | string }).rejected;
    const approved = getBooleanLikeValue(mockup.approved);

    // API sometimes indicates approved via rejected=false instead of approved=1.
    const approvedViaRejectedFlag =
      rejected === false ||
      rejected === 0 ||
      rejected === "0" ||
      mockup.is_rejected === false;

    return !isRejectedMockup(mockup) && (approved || approvedViaRejectedFlag);
  };

  const getMockupSortRank = (mockup: OrderMockup) => {
    if (isRejectedMockup(mockup)) return 2;
    if (isApprovedMockup(mockup)) return 1;
    return 0;
  };

  const sortedMockups = [...mockups].sort((a, b) => {
    return getMockupSortRank(a) - getMockupSortRank(b);
  });

  return (
    <Box
      sx={{
        padding: { xl: "36px", lg: "36px", md: "36px", sm: "20px", xs: "10px" },
        paddingTop: {
          xl: "30px",
          lg: "30px",
          md: "30px",
          sm: "20px",
          xs: "10px",
        },
        borderRadius: "4px",
        border: `1px solid ${theme.palette.customColors.disabledBorderColor}`,
        backgroundColor: theme.palette.customColors.greyBodyBackground,
      }}
    >
      <RadioGroup>
        {sortedMockups.map((mockup: OrderMockup) => (
          <Box
            key={mockup.id}
            sx={{
              height: "300px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "25px",
              paddingTop: "14px",
              paddingBottom: "14px",
              borderRadius: "4px",
              border:
                mockup.id === selectedMockup
                  ? `2px solid ${theme.palette.customColors.lightPurple}`
                  : `2px solid ${theme.palette.customColors.greyBorder}`,
              cursor: "pointer",
              mb: 2,
              position: "relative",
              backgroundColor: theme.palette.customColors.white,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                border: `2px solid ${theme.palette.customColors.purple}`,
              },
              "@media (min-width:1536px) and (max-width:1650px)": {
                height: "270px",
              },
              "@media (max-width:400px)": {
                width: "100%",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <FormControlLabel
                value={mockup.id}
                control={
                  <Radio
                    color="primary"
                    checked={mockup.priority === 1}
                    onChange={(e) =>
                      handleMockupPriorityChange(Number(mockup.id))
                    }
                  />
                }
                label="Display"
                sx={{
                  flex: 1,
                  position: "absolute",
                  top: 8,
                  left: 25,
                  zIndex: 1,
                }}
                disabled={
                  archived === 1 || Boolean(mockup.is_rejected) === true
                }
              />
              <IconButton
                color="secondary"
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 25,
                  zIndex: 1,
                  backgroundColor: mockup.approved
                    ? theme.palette.customColors.greenLight
                    : "",
                  p: 1.5,
                  height: 24,
                  width: 24,
                }}
              >
                <img
                  src={
                    mockup.is_rejected
                      ? rejectIcon
                      : mockup.approved
                        ? checkIcon
                        : pendingIcon
                  }
                  style={{
                    height: mockup.approved ? "14px" : "24px",
                    width: mockup.approved ? "14px" : "24px",
                  }}
                  alt="check icon"
                />
              </IconButton>
            </Box>

            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                "&:hover .overlay-button": {
                  opacity: 1,
                },
              }}
            >
              {isPdfUrl(mockup.image_url) ? (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 200,
                    height: {
                      xs: 120,
                      sm: 150,
                      md: 180,
                      lg: 185,
                      xl: 190,
                    },
                    "@media (min-width:1536px) and (max-width:1650px)": {
                      height: 160,
                    },
                    borderRadius: "4px",
                    overflow: "hidden",
                    bgcolor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <Box
                    component="iframe"
                    src={mockup.image_url}
                    title="Mockup PDF preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: 0,
                      display: "block",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={mockup.image_url}
                  alt={mockup.image_url}
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    maxWidth: 200,
                    maxHeight: {
                      xs: 120,
                      sm: 150,
                      md: 180,
                      lg: 185,
                      xl: 190,
                    },
                    "@media (min-width:1536px) and (max-width:1650px)": {
                      maxHeight: 160,
                    },
                  }}
                />
              )}
              {mockup.approved === 0 && !mockup.is_rejected && (
                <Box
                  className="overlay-button"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {/* Edit (manual file replace) — kept for easy restore; use Generate tab instead
                  <Box
                    sx={{
                      backgroundColor:
                        archived === 1
                          ? "rgba(0, 0, 0, 0.3)"
                          : "rgba(0, 0, 0, 0.5)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 40,
                      cursor: archived === 1 ? "not-allowed" : "pointer",
                      "&:hover": {
                        backgroundColor:
                          archived === 1
                            ? "rgba(0, 0, 0, 0.3)"
                            : "rgba(0, 0, 0, 0.5)",
                      },
                    }}
                    onClick={() => {
                      if (archived !== 1) {
                        document
                          .getElementById(`fileInput-${mockup.id}`)
                          ?.click();
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faEdit}
                      fontSize="0.9rem"
                      color="white"
                      style={{ marginRight: "4px" }}
                    />
                    <CustomTypography
                      variant="heading6"
                      color="white"
                      fontWeight={600}
                    >
                      Edit
                    </CustomTypography>
                  </Box>
                  */}

                  {onGenerateMockupClick && (
                    <Box
                      sx={{
                        backgroundColor:
                          archived === 1
                            ? "rgba(0, 0, 0, 0.3)"
                            : "rgba(0, 0, 0, 0.5)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 90,
                        height: 40,
                        cursor: archived === 1 ? "not-allowed" : "pointer",
                      }}
                      onClick={() => {
                        if (archived !== 1) {
                          onGenerateMockupClick(mockup.id!);
                        }
                      }}
                    >
                      <CustomTypography
                        variant="heading6"
                        color="white"
                        fontWeight={600}
                      >
                        Edit
                      </CustomTypography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 40,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                    onClick={() => openImagePreview(mockup.image_url)}
                  >
                    <FontAwesomeIcon
                      icon={faEye}
                      fontSize="0.9rem"
                      color="white"
                      style={{ marginRight: "4px" }}
                    />
                    <CustomTypography
                      variant="heading6"
                      color="white"
                      fontWeight={600}
                    >
                      View
                    </CustomTypography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 20,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                zIndex: 1,
              }}
            >
              <Box>
                <IconButton
                  color="secondary"
                  sx={{
                    backgroundColor:
                      theme.palette.customColors.greyBodyBackground,
                    p: 1,
                    height: 24,
                    width: 24,
                  }}
                  onClick={() => handleCommentClick(mockup.id!)}
                >
                  <img
                    src={msgIcon}
                    style={{ height: "24px", width: "24px" }}
                    alt="msgIcon"
                  />
                </IconButton>

                <input
                  type="file"
                  multiple
                  onChange={(event) =>
                    handleFileChangeAndUploadForMockup(event, mockup.id!)
                  }
                  style={{ display: "none" }}
                  id={`fileInput-${mockup.id}`}
                />
              </Box>

              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <CustomTypography variant="heading7" fontWeight={600}>
                        Mockup Info
                      </CustomTypography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          minWidth: "200px",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          Upload date:
                        </CustomTypography>
                        <CustomTypography variant="heading8" fontWeight={600}>
                          {mockup.created_date
                            ? dayjs(mockup.created_date).format("DD MMM YYYY")
                            : "-"}
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          Uploaded by:
                        </CustomTypography>
                        <CustomTypography variant="heading8" fontWeight={600}>
                          {mockup.admin_name || "-"}
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          Last changed date:
                        </CustomTypography>
                        <CustomTypography variant="heading8" fontWeight={600}>
                          {mockup.image_url_last_changed_at
                            ? dayjs(mockup.image_url_last_changed_at).format(
                              "DD MMM YYYY",
                            )
                            : "-"}
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          Iterations:
                        </CustomTypography>
                        <CustomTypography variant="heading8" fontWeight={600}>
                          {mockup.image_url_change_count || "-"}
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          Approved date:
                        </CustomTypography>
                        <CustomTypography variant="heading8" fontWeight={600}>
                          {mockup.approved_date
                            ? dayjs(mockup.approved_date).format("DD MMM YYYY")
                            : "-"}
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Box>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: theme.palette.customColors.blackBorder,
                      width: "250px",
                    },
                  },
                }}
              >
                <IconButton
                  color="secondary"
                  sx={{
                    p: 1,
                    height: 24,
                    width: 24,
                  }}
                >
                  <img
                    src={infoIcon}
                    style={{ height: "24px", width: "24px" }}
                    alt="infoIcon"
                  />
                </IconButton>
              </Tooltip>

              {/* <IconButton
                color="secondary"
                sx={{
                  backgroundColor: theme.palette.customColors.pinkLight,
                  p: 1.5,
                  height: 24,
                  width: 24,
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
                onClick={() => {
                  handleDeleteClick(mockup.id!);
                }}
                disabled={
                  archived === 1 ||
                  !canDeleteMockup ||
                  Boolean(mockup.approved) === true ||
                  Boolean(mockup.is_rejected) === true
                }
              >
                <DeleteIcon
                  sx={{
                    color: theme.palette.customColors.pinkDark,
                    width: 18,
                    height: 18,
                  }}
                />
              </IconButton> */}
              {userStore.hasPermission(
                [
                  EmployeeRoles.ADMIN_MOCKUP_APPROVE_REJECT,
                  EmployeeRoles.REJECT_APPROVED_MOCKUPS,
                ],
                true,
              ) && (
                  <>
                    <IconButton
                      color="secondary"
                      sx={{
                        p: 1.5,
                        height: 30,
                        width: 30,
                        "&.Mui-disabled": {
                          opacity: 0.5,
                        },
                      }}
                      onClick={() => {
                        handleOpenRejectPopup(mockup.id!);
                      }}
                      disabled={
                        archived === 1 ||
                        (Boolean(mockup.approved) === true &&
                          !userStore.hasPermission(
                            EmployeeRoles.REJECT_APPROVED_MOCKUPS,
                            true,
                          )) ||
                        Boolean(mockup.is_rejected) === true
                      }
                    >
                      <UnpublishedOutlinedIcon
                        sx={{
                          color: theme.palette.customColors.pinkDark,
                          width: 25,
                          height: 25,
                        }}
                      />
                    </IconButton>

                    <IconButton
                      color="secondary"
                      sx={{
                        p: 1.5,
                        height: 30,
                        width: 30,
                        "&.Mui-disabled": {
                          opacity: 0.5,
                        },
                      }}
                      onClick={() => {
                        if (archived !== 1) {
                          handleOpenApprovalPopup(mockup.id!);
                        }
                      }}
                      disabled={
                        archived === 1 ||
                        Boolean(mockup.approved) === true ||
                        Boolean(mockup.is_rejected) === true
                      }
                    >
                      <TaskAltIcon
                        sx={{
                          color: theme.palette.customColors.darkBlue,
                          width: 25,
                          height: 25,
                        }}
                      />
                    </IconButton>
                  </>
                )}
            </Box>
          </Box>
        ))}
      </RadioGroup>
      <Box
        sx={{
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          mb: 2,
          backgroundColor: theme.palette.customColors.white,
          position: "relative",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={emptyImage} alt="emptyImage" />
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: 30,
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {onGenerateClick && (
                <AppButton
                  variant="primary"
                  label="Upload Mockups"
                  onClick={onGenerateClick}
                  disabled={archived === 1}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {userStore.hasPermission(EmployeeRoles.ADMIN_MOCKUP_APPROVE_REJECT) &&
        popupState && (
          <Dialog
            fullWidth
            maxWidth="sm"
            open={true}
            onClose={handleCloseApprovalPopup}
          >
            <DialogTitle>
              {popupState.mode === "approve"
                ? "Approve Mockup"
                : "Reject Mockup"}
            </DialogTitle>
            <DialogContent>
              {(popupState.mode === "approve" ||
                popupState.mode === "reject") && (
                  <Box sx={{ mb: 3 }}>
                    <ImageUpload
                      id={
                        popupState.mode === "approve"
                          ? "approval-image-upload"
                          : "reject-image-upload"
                      }
                      size="large"
                      showImagesList={true}
                      newImageList={projectSourcingOpsStore.approvalImages}
                      addImagesToBeUploaded={(files: File[]) =>
                        projectSourcingOpsStore.setApprovalImages(files)
                      }
                      removeImage={(image) =>
                        projectSourcingOpsStore.setApprovalImages(
                          projectSourcingOpsStore.approvalImages.filter(
                            (file: File) => file.name !== image.title,
                          ),
                        )
                      }
                      uploadText="Upload Image"
                      minHeight="150px"
                      multiple={false}
                    />
                  </Box>
                )}

              <Box>
                <FormTextField
                  borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                  multiline
                  id={
                    popupState.mode === "approve"
                      ? "approval_note"
                      : "reject_note"
                  }
                  value={
                    popupState.mode === "approve"
                      ? projectSourcingOpsStore.approvalNote
                      : projectSourcingOpsStore.rejectionNote
                  }
                  placeholder={
                    popupState.mode === "approve"
                      ? "Write approval note..."
                      : "Write rejection note..."
                  }
                  onChange={(id, value) => {
                    if (popupState.mode === "approve") {
                      projectSourcingOpsStore.setApprovalNotes(value || "");
                    } else {
                      projectSourcingOpsStore.setRejectionNotes(value || "");
                    }
                  }}
                  rows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{ pb: 2, display: "flex", justifyContent: "center", gap: 2 }}
            >
              <AppButton
                variant="grayscale"
                label="Cancel"
                onClick={handleCloseApprovalPopup}
                customStyles={{
                  minWidth: "120px",
                }}
              />
              {popupState.mode === "approve" ? (
                <AppButton
                  label="Approve"
                  customStyles={{
                    minWidth: "120px",
                  }}
                  onClick={() => {
                    if (!popupState || !popupState.mockupId) {
                      showError("Error", "No mockup selected for approval");
                      return;
                    }
                    if (!projectSourcingOpsStore.approvalNote.trim()) {
                      showError("Error", "Please enter note before approving");
                      return;
                    }
                    if (projectSourcingOpsStore.approvalImages.length === 0) {
                      showError(
                        "Error",
                        "Please upload an image before approving",
                      );
                      return;
                    }
                    if (projectSourcingOpsStore.approvalImages.length > 1) {
                      showError(
                        "Error",
                        "Please upload only one image for approval",
                      );
                      return;
                    }

                    const imageAPI = new ImageAPI();
                    imageAPI
                      .uploadImage(
                        projectSourcingOpsStore.approvalImages,
                        "product-images",
                      )
                      .then((uploadedUrls) => {
                        const imageUrl = Array.isArray(uploadedUrls)
                          ? uploadedUrls[0]
                          : uploadedUrls;
                        handleApproveMockup(
                          popupState.mockupId,
                          imageUrl,
                          projectSourcingOpsStore.approvalNote.trim(),
                        );
                        handleCloseApprovalPopup();
                      })
                      .catch((error) => {
                        showError(
                          "Error occurred",
                          error?.response?.data?.message ||
                          "Failed to upload image. Please try again.",
                        );
                      });
                  }}
                  disabled={
                    !popupState?.mockupId ||
                    !projectSourcingOpsStore.approvalNote.trim() ||
                    projectSourcingOpsStore.approvalImages.length === 0
                  }
                />
              ) : (
                <AppButton
                  variant="secondary"
                  label="Reject"
                  customStyles={{
                    minWidth: "120px",
                  }}
                  onClick={() => {
                    if (!popupState || !popupState.mockupId) {
                      showError("Error", "No mockup selected for rejection");
                      return;
                    }
                    if (!projectSourcingOpsStore.rejectionNote?.trim()) {
                      showError("Error", "Please enter note before rejecting");
                      return;
                    }
                    if (projectSourcingOpsStore.approvalImages.length === 0) {
                      showError(
                        "Error",
                        "Please upload an image before rejecting",
                      );
                      return;
                    }
                    if (projectSourcingOpsStore.approvalImages.length > 1) {
                      showError(
                        "Error",
                        "Please upload only one image for rejection",
                      );
                      return;
                    }

                    const imageAPI = new ImageAPI();
                    imageAPI
                      .uploadImage(
                        projectSourcingOpsStore.approvalImages,
                        "mockup-approval-evidence",
                      )
                      .then((uploadedUrls) => {
                        const imageUrl = Array.isArray(uploadedUrls)
                          ? uploadedUrls[0]
                          : uploadedUrls;
                        handleRejectMockup(
                          popupState.mockupId,
                          projectSourcingOpsStore.rejectionNote.trim(),
                          imageUrl,
                        );
                        handleCloseApprovalPopup();
                      })
                      .catch((error) => {
                        showError(
                          "Error occurred",
                          error?.response?.data?.message ||
                          "Failed to upload image. Please try again.",
                        );
                      });
                  }}
                  disabled={
                    !popupState?.mockupId ||
                    !projectSourcingOpsStore.rejectionNote?.trim() ||
                    projectSourcingOpsStore.approvalImages.length === 0
                  }
                />
              )}
            </DialogActions>
          </Dialog>
        )}
    </Box>
  );
};

export default observer(SourcingOpsProductMockups);
